import isNil from 'lodash/isNil'

import { useOutpost } from '../components/OutpostProvider/OutpostProvider'
import { getAppliedValue } from './gift-card-helpers'
import { sumByField } from './reducer-utils'
import { DINING_OPTION_BEHAVIORS } from './dining-options'
import { useGiftCardEnabled } from '../components/IfGiftCard/IfGiftCard'
import { useGiftCard } from '../components/GiftCardProvider/GiftCardProvider'
import { useCallback } from 'react'

const toDollars = number => +(+number).toFixed(2)

const getGiftCardBalance = ({ giftCard, cart }) => {
  if (cart.order.giftCard) {
    return cart.order.giftCard.appliedBalance || 0
  }
  return getAppliedValue({ giftCard, cart })
}

export const useLineItems = ({
  cart,
  showTip,
  showTotal,
  deliveryChargeLabel = 'Delivery Cost'
}) => {
  const giftCardEnabled = useGiftCardEnabled()
  const { isOutpost } = useOutpost()
  const { giftCard } = useGiftCard()

  return useCallback(tip => {
    const hasTip = !isNil(tip)
    const serverTip = cart.order.tip || 0
    const displayTip = hasTip ? tip : serverTip
    let displayTotal = cart.order.total
    let displaySubTotal = cart.order.subtotal || 0
    // Account for case where a tip is specified, but the cart also has a tip
    // eg. When place order fails due to an out-of-stock item
    if (hasTip) {
      displayTotal = displayTotal - serverTip + tip
    }

    const { order: { discounts = {} } } = cart

    const { restaurantDiscount, loyaltyDiscount } = discounts

    const showPromoAmount = Boolean(restaurantDiscount)
    const showLoyaltyAmount = Boolean(loyaltyDiscount)

    // if a discount has been applied, calculate the displayed subtotal
    // based on the selections' pre discount price
    if (showPromoAmount || showLoyaltyAmount) {
      displaySubTotal = sumByField('preDiscountPrice')(cart.order.selections)
    }

    const giftCardBalance = getGiftCardBalance({ cart, giftCard })
    const showGiftCard = Boolean(giftCardBalance > 0)

    const totals = [{
      label: 'Subtotal',
      amount: toDollars(displaySubTotal),
      id: 'subtotal-amount'
    }]
    if (showLoyaltyAmount) {
      totals.push({
        label: 'Reward Applied',
        amount: toDollars(-loyaltyDiscount.amount),
        id: 'loyalty-reward-amount'
      })
    }
    if (showPromoAmount) {
      totals.push({
        label: 'Promo Applied',
        amount: toDollars(-restaurantDiscount.amount),
        id: 'promo-code-amount'
      })
    }
    if (cart.order.deliveryChargeTotal > 0 || cart.diningOptionBehavior === DINING_OPTION_BEHAVIORS.DELIVERY) {
      totals.push({
        label: deliveryChargeLabel,
        amount: toDollars(cart.order.deliveryChargeTotal),
        id: 'deliveryChargeTotal-amount'
      })
    }
    if (cart.order.serviceChargeTotal > 0) {
      totals.push({
        label: 'Service Charges',
        amount: toDollars(cart.order.serviceChargeTotal),
        id: 'Service-charge-amount'
      })
    }
    if (cart.order.tax > 0) {
      totals.push({
        label: 'Tax',
        amount: toDollars(cart.order.tax),
        id: 'tax-amount'
      })
    }
    if (isOutpost && cart.order.outpostFee) {
      totals.push({
        label: 'Toast Drop Fee',
        amount: toDollars(cart.order.outpostFee),
        id: 'outpost-fee-amount'
      })
    }
    if (showTip) {
      totals.push({
        label: 'Tip',
        amount: toDollars(displayTip),
        id: 'tip-amount'
      })
    }
    if (showTotal) {
      totals.push({
        label: 'Total',
        amount: toDollars(displayTotal),
        id: 'total-order-amount'
      })
    }
    if (giftCardEnabled) {
      if (showGiftCard) {
        totals.push({
          label: 'Gift Card Applied',
          amount: toDollars(-giftCardBalance),
          id: 'gift-card-amount'
        })
      }
      if (displayTotal - giftCardBalance > 0 && showGiftCard) {
        totals.push({
          label: 'Total Remaining',
          amount: toDollars(displayTotal - giftCardBalance),
          id: 'total-order-remaining-amount'
        })
      }
    }

    return totals
  }, [cart, deliveryChargeLabel, giftCard, giftCardEnabled, isOutpost, showTip, showTotal])
}

export const isDaasCart = cart => !!(cart && cart.deliveryProviderInfo && cart.deliveryProviderInfo.provider !== 'TOAST')

export const getDaasAwareDiningOption = (cart) => {
  switch (cart.diningOptionBehavior) {
    case DINING_OPTION_BEHAVIORS.TAKE_OUT:
      return DINING_OPTION_BEHAVIORS.TAKE_OUT
    case DINING_OPTION_BEHAVIORS.DELIVERY:
      if (isDaasCart(cart)) {
        return 'TDS'
      }
      return '1PD'
    default:
      return null
  }
}
